/**
 * Types for merchant bulk RMA actions (Mark as Done): start payload, new-report payload,
 * and per-return reason codes for the status panel.
 */
export enum BULK_RMA_ACTION {
    MARK_DONE = "mark_done",
}

export enum BULK_REQUEST_STATUS {
    PROCESSING = "processing",
    COMPLETED = "completed",
    CANCELLED = "cancelled",
}

export interface IBulkRequestCounts {
    total: number;
    pending: number;
    allocated: number;
    processing: number;
    succeeded: number;
    blocked: number;
    error: number;
    cancelled: number;
}

/** reason_code -> count of failed returns */
export type IBulkRequestReasonCounts = Record<string, number>;

export interface IBulkNewReport {
    request_id: number;
    status: BULK_REQUEST_STATUS;
    action: BULK_RMA_ACTION;
    chunk_size: number;
    counts: IBulkRequestCounts;
    counts_by_reason: IBulkRequestReasonCounts;
    last_progress_at: string | null;
    finished_at: string | null;
}

/** Terminal per-return outcome from `GET .../bulk-actions/{requestId}/returns`. */
export interface IBulkRequestReturnOutcome {
    rma_id: number;
    status: string;
    reason_code: string | null;
    reason_message: string | null;
}

/** Row metadata group (`succeeded` or a bulk `reason_code`). */
export type IBulkReportGroup = "succeeded" | string;

/** Mark-as-done failure reason codes from the bulk API (aligned with shopify-app MarkDoneReasonCode). */
export enum BULK_MARK_DONE_REASON_CODE {
    NOT_APPROVED = "not_approved",
    VALIDATION_INCOMPLETE = "validation_incomplete",
    REFUND_INCOMPLETE = "refund_incomplete",
    EXCHANGE_NOT_HANDLED = "exchange_not_handled",
    PAYMENT_AUTHORIZATION_PENDING = "payment_authorization_pending",
    POLICY_NOT_CONFIGURED = "policy_not_configured",
    ALREADY_DONE = "already_done",
    OPERATION_LOCKED = "operation_locked",
    CONCURRENT_OPERATION = "concurrent_operation",
    RMA_NOT_FOUND = "rma_not_found",
    EXECUTION_ERROR = "execution_error",
    JOB_FAILED = "job_failed",
    CANCELLED = "cancelled",
}

/** Top-level bulk status panel sections. */
export enum BULK_STATUS_PANEL_BUCKET {
    MARKED_AS_DONE = "marked_as_done",
    STILL_OPEN = "still_open",
    ERROR = "error",
    NOT_PROCESSED = "not_processed",
}

const REASON_PANEL_BUCKET: Record<
    BULK_MARK_DONE_REASON_CODE,
    BULK_STATUS_PANEL_BUCKET
> = {
    [BULK_MARK_DONE_REASON_CODE.NOT_APPROVED]:
        BULK_STATUS_PANEL_BUCKET.STILL_OPEN,
    [BULK_MARK_DONE_REASON_CODE.VALIDATION_INCOMPLETE]:
        BULK_STATUS_PANEL_BUCKET.STILL_OPEN,
    [BULK_MARK_DONE_REASON_CODE.REFUND_INCOMPLETE]:
        BULK_STATUS_PANEL_BUCKET.STILL_OPEN,
    [BULK_MARK_DONE_REASON_CODE.EXCHANGE_NOT_HANDLED]:
        BULK_STATUS_PANEL_BUCKET.STILL_OPEN,
    [BULK_MARK_DONE_REASON_CODE.PAYMENT_AUTHORIZATION_PENDING]:
        BULK_STATUS_PANEL_BUCKET.STILL_OPEN,
    [BULK_MARK_DONE_REASON_CODE.POLICY_NOT_CONFIGURED]:
        BULK_STATUS_PANEL_BUCKET.STILL_OPEN,
    [BULK_MARK_DONE_REASON_CODE.OPERATION_LOCKED]:
        BULK_STATUS_PANEL_BUCKET.STILL_OPEN,
    [BULK_MARK_DONE_REASON_CODE.CONCURRENT_OPERATION]:
        BULK_STATUS_PANEL_BUCKET.STILL_OPEN,
    [BULK_MARK_DONE_REASON_CODE.ALREADY_DONE]:
        BULK_STATUS_PANEL_BUCKET.NOT_PROCESSED,
    [BULK_MARK_DONE_REASON_CODE.RMA_NOT_FOUND]: BULK_STATUS_PANEL_BUCKET.ERROR,
    [BULK_MARK_DONE_REASON_CODE.EXECUTION_ERROR]:
        BULK_STATUS_PANEL_BUCKET.ERROR,
    [BULK_MARK_DONE_REASON_CODE.JOB_FAILED]:
        BULK_STATUS_PANEL_BUCKET.NOT_PROCESSED,
    [BULK_MARK_DONE_REASON_CODE.CANCELLED]:
        BULK_STATUS_PANEL_BUCKET.NOT_PROCESSED,
};

export function getBulkReasonPanelBucket(
    reasonCode: string
): BULK_STATUS_PANEL_BUCKET {
    const bucket =
        REASON_PANEL_BUCKET[reasonCode as BULK_MARK_DONE_REASON_CODE];
    return bucket ?? BULK_STATUS_PANEL_BUCKET.ERROR;
}

export interface IStartBulkActionResponse {
    request_id: number;
    status: BULK_REQUEST_STATUS;
    total: number;
}

/** Committed list filters sent with filter-scoped bulk start (no pagination/sort). */
export interface IBulkSelectionFilters {
    search_input: string;
    search_exact_match: boolean;
    filter_resolution_id: number | null;
    filter_return_reason_id: number | null;
    filter_restock_validated_type: string;
    filter_date_range: string | null;
    filter_status?: string;
    filter_reserve_item_expiration: string;
    filter_reserve_item_expiration_input: string;
    filter_payment_status_type: string;
    filter_manual_actions: string[];
    filter_error_labels: string[];
    filter_on_track: string[];
    status_group: string;
}

export type IStartBulkActionPayload =
    | { rma_ids: number[] }
    | { filters: IBulkSelectionFilters; excluded_rma_ids: number[] };

/** Start outcome; failures are shown by the global HTTP error modal, not typed here. */
export type IStartBulkActionResult =
    | { started: true; response: IStartBulkActionResponse }
    | { started: false };

const KNOWN_REASON_CODES: readonly string[] = Object.values(
    BULK_MARK_DONE_REASON_CODE
);

export function reasonTranslationKey(reasonCode: string): string {
    return KNOWN_REASON_CODES.includes(reasonCode)
        ? `BULK_REQUEST_REASON_${reasonCode.toUpperCase()}`
        : "BULK_REQUEST_REASON_UNKNOWN";
}
